import { injectToolCSS } from "../utils/cssGenerator";
import isDark from "../utils/isDark";
import { t } from "../i18n/translate";
import { getFullSentence } from "../utils/dom";
import { speak, stopSpeaking } from "../utils/speech";

import { 
    userSettings
} from '@/globals/userSettings';

let lastHighlightedElement: HTMLElement | null = null;

function resetHighlight(element: HTMLElement | null) {
  if (element) {
    element.classList.remove('asw-reading');
    const orgColor = element.getAttribute('data-asw-orgColor');
    if (orgColor) {
      element.style.color = orgColor;
      element.removeAttribute('data-asw-orgColor');
    }
  }
}

function handleSpeak(text: string, element: HTMLElement, highlight: boolean = true) {
  const settings = userSettings;
  const lang = settings.lang || 'en';

  resetHighlight(lastHighlightedElement);

  speak(text, {
    lang,
    onStart: () => {
      if (highlight && element) {
        element.classList.add('asw-reading');

        // Contrast logic: if text is dark, make it white
        const computedStyle = window.getComputedStyle(element);
        const currentColor = computedStyle.color;
        if (isDark(currentColor)) {
          element.setAttribute('data-asw-orgColor', element.style.color || '');
          element.style.setProperty('color', '#ffffff', 'important');
        }
        lastHighlightedElement = element;
      }
    },
    onEnd: () => {
      if (highlight) resetHighlight(element);
    },
    onError: () => {
      if (highlight) resetHighlight(element);
    }
  });
}

export const screenReaderConfig = {
  id: 'screen-reader',
  enable: false,
  css: `
        .asw-reading {
            outline: 2px solid #ffeb3b !important;
            outline-offset: 2px !important;
            background-color: rgba(255, 235, 59, 0.45) !important;
            transition: outline 0.2s ease, background-color 0.2s ease !important;
            border-radius: 4px !important;
            box-shadow: 0 0 10px rgba(0,0,0,0.1) !important;
        }
    `
};

export default function screenReader(enable = false) {
  if (!('speechSynthesis' in window)) {
    if (enable) alert(t("Sorry, your browser does not support the Screen Reader feature (Speech Synthesis)."));
    return;
  }

  injectToolCSS({
    ...screenReaderConfig,
    enable
  });

  if (enable) {
    (window as any).__asw__onClickScreenReader = (event: MouseEvent) => {
      let clickedElement = event.target as HTMLElement;

      // Don't read if clicking on the widget itself or any of its children
      if (clickedElement.closest('.asw-menu') || clickedElement.closest('.asw-menu-btn') || clickedElement.closest('.asw-widget') || clickedElement.closest('.asw-structure-modal')) {
        return;
      }

      // "Look through" strategy: If the clicked element is a container or a link, 
      // check if there's something more specific underneath it (e.g., a heading covered by a stretched link).
      const targetsToLookThrough = ['A', 'DIV', 'SECTION', 'ARTICLE'];
      if (targetsToLookThrough.includes(clickedElement.nodeName)) {
        const originalPointerEvents = clickedElement.style.pointerEvents;
        clickedElement.style.pointerEvents = 'none';
        
        const elementUnderneath = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement;
        clickedElement.style.pointerEvents = originalPointerEvents;

        // If something more specific is underneath and it's not a parent/ancestor of the clicked element
        // (which would happen if we clicked a transparent part of a container)
        if (elementUnderneath && elementUnderneath !== clickedElement && !clickedElement.contains(elementUnderneath) && !elementUnderneath.contains(clickedElement)) {
            clickedElement = elementUnderneath;
        }
      }

      const ignoredElements = ["BODY", "HEAD", "HTML"];
      if (!ignoredElements.includes(clickedElement.nodeName)) {
        event.preventDefault();
        event.stopPropagation();

        const selectedText = getFullSentence(clickedElement);
        handleSpeak(selectedText, clickedElement);
      }
    };

    document.addEventListener('click', (window as any).__asw__onClickScreenReader, true);
  } else {
    stopSpeaking();

    // Only speak the disabled message if it was actually enabled before (optional, but cleaner)
    if ((window as any).__asw__onClickScreenReader) {
      document.removeEventListener('click', (window as any).__asw__onClickScreenReader, true);
      delete (window as any).__asw__onClickScreenReader;
    }

    resetHighlight(lastHighlightedElement);
  }
}
